import React from 'react';
import PropTypes from 'prop-types';
import {
  LuArrowLeftRight, LuBadgeCheck, LuBan, LuBuilding2, LuCalendarClock, LuCalendarDays, LuClock, LuCoins, LuFile, LuFileImage,
  LuFileSpreadsheet, LuFileText, LuFingerprint, LuHash, LuLayers, LuMail, LuPencil, LuPercent, LuReceipt, LuRows3, LuTag, LuTrash2, LuWallet,
} from 'react-icons/lu';
import Badge from '../../../components/ui/Badge';
import DetailPane from '../../../components/data/DetailPane';
import useBudgetText from '../budgetText';
import { pick } from '../budgetColumns';
import { ActiveBadge, PaymentBadge, initialsOf, isIncome, isPassive, partyName } from './BudgetCell';

const text = (value) => (value === undefined || value === null || value === '' ? '' : String(value));

/** Icon for an attachment by its extension (as the old list did). */
const fileIcon = (fileName) => {
  const ext = String(fileName || '').split('.').pop().toLowerCase();
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(ext)) return LuFileImage;
  if (ext === 'pdf' || ['doc', 'docx'].includes(ext)) return LuFileText;
  if (['xls', 'xlsx', 'csv'].includes(ext)) return LuFileSpreadsheet;
  return LuFile;
};

/**
 * The selected income / expense (replaces the old action bar): change the
 * payment status, edit, delete (admins), set active / inactive, every invoice
 * field and the attachments (open in a new tab). Docked beside the table on
 * wide screens, a drawer otherwise.
 */
const BudgetDetailPane = ({
  docked, height, budget, isAdmin, canToggleStatus, getEmployeeName, position, onPrev, onNext, onClose, onToggleStatus, onEdit, onDelete, onToggleActive,
}) => {
  const { label, formatAmount, formatInt, formatDate } = useBudgetText();
  const paneLabel = label('panel.label');

  if (!budget) return <DetailPane docked={docked} height={height} open={false} label={paneLabel} onClose={onClose} />;

  const income = isIncome(budget);
  const passive = isPassive(budget);
  const party = income ? 'Musteri' : 'Gonderici';
  const currency = text(budget['ParaBirimi']);
  const money = (value) => {
    const formatted = formatAmount(value);
    return formatted === '-' ? '' : `${formatted}${currency ? ` ${currency}` : ''}`;
  };
  const amountText = formatAmount(pick(budget, 'Tutar', 'BütçeMiktarı'));
  const attachments = budget.attachments || [];

  const details = [
    { key: 'party', icon: LuBuilding2, label: label(`columns.${income ? 'customerName' : 'senderName'}`), value: partyName(budget, getEmployeeName) },
    { key: 'tax', icon: LuFingerprint, label: label(`columns.${income ? 'customerTax' : 'senderTax'}`), value: text(budget[`${party}TcknVkn`]), copyable: Boolean(budget[`${party}TcknVkn`]) },
    { key: 'tag', icon: LuMail, label: label(`columns.${income ? 'customerTag' : 'senderTag'}`), value: text(budget[`${party}Etiketi`]) },
    { key: 'amount', icon: LuWallet, label: label('columns.amount'), value: money(pick(budget, 'Tutar', 'BütçeMiktarı')) },
    { key: 'documentDate', icon: LuCalendarDays, label: label('columns.documentDate'), value: formatDate(pick(budget, 'BelgeTarihi', 'BütçeTarihi')) },
    // Attachments right under the amount and date (the old action bar showed them up front)
    ...attachments.map((file, index) => ({
      key: `attachment-${index}`,
      icon: fileIcon(file.name),
      label: file.size
        ? label('panel.attachmentSize', { index: index + 1, count: attachments.length, size: (file.size / 1024).toFixed(1) })
        : label('panel.attachment', { index: index + 1, count: attachments.length }),
      value: text(file.name),
      href: file.url,
      external: true,
    })),
    { key: 'documentTime', icon: LuClock, label: label('columns.documentTime'), value: text(budget['BelgeZamani']) },
    { key: 'createdDate', icon: LuCalendarClock, label: label('columns.createdDate'), value: formatDate(budget['OlusturmaTarihi']) },
    { key: 'createdTime', icon: LuClock, label: label('columns.createdTime'), value: text(budget['OlusturmaZamani']) },
    { key: 'gib', icon: LuHash, label: label('columns.gib'), value: text(budget['GibNumarasi']), copyable: Boolean(budget['GibNumarasi']) },
    { key: 'draft', icon: LuHash, label: label('columns.draft'), value: text(budget['TaslakNo']) },
    { key: 'scenario', icon: LuLayers, label: label('columns.scenario'), value: text(budget['Senaryo']) },
    { key: 'invoiceType', icon: LuReceipt, label: label('columns.invoiceType'), value: text(budget['FaturaTipi']) },
    { key: 'category', icon: LuTag, label: label('columns.category'), value: text(budget['BütçeKategorisi']) },
    { key: 'ettn', icon: LuFingerprint, label: label('columns.ettn'), value: text(budget['Ettn']), copyable: Boolean(budget['Ettn']) },
    { key: 'envelopeUuid', icon: LuMail, label: label('columns.envelopeUuid'), value: text(budget['ZarfUuid']), copyable: Boolean(budget['ZarfUuid']) },
    { key: 'envelopeStatus', icon: LuBadgeCheck, label: label('columns.envelopeStatus'), value: text(budget['ZarfDurumu']) },
    { key: 'discount', icon: LuPercent, label: label('columns.discount'), value: money(budget['Iskonto']) },
    { key: 'extraCosts', icon: LuCoins, label: label('columns.extraCosts'), value: money(budget['FaturaDisiMasraflari']) },
    { key: 'lineCount', icon: LuRows3, label: label('columns.lineCount'), value: formatInt(budget['SatirSayisi']) },
    { key: 'lineTotal', icon: LuCoins, label: label('columns.lineTotal'), value: money(budget['BirimFiyatMiktarTutari']) },
    { key: 'payable', icon: LuWallet, label: label('columns.payable'), value: money(budget['OdenecekTutar']) },
    { key: 'taxExcluded', icon: LuCoins, label: label('columns.taxExcluded'), value: money(budget['VergilerHaricTutar']) },
    { key: 'taxIncluded', icon: LuCoins, label: label('columns.taxIncluded'), value: money(budget['VergilerDahilTutar']) },
  ];

  const actions = [
    {
      key: 'status',
      label: label('panel.toggleStatus'),
      title: canToggleStatus ? label('panel.toggleStatus') : label('panel.statusLocked'),
      icon: LuArrowLeftRight,
      tint: 'amber',
      disabled: !canToggleStatus,
      onClick: onToggleStatus,
    },
    { key: 'edit', label: label('panel.edit'), icon: LuPencil, tint: 'blue', onClick: onEdit },
  ];
  if (isAdmin) actions.push({ key: 'delete', label: label('panel.delete'), title: label('panel.deleteHint'), icon: LuTrash2, tint: 'rose', onClick: onDelete });

  return (
    <DetailPane
      docked={docked}
      height={height}
      open
      label={paneLabel}
      heading={label('panel.heading')}
      position={position}
      contentKey={budget.id}
      identity={{
        avatar: initialsOf(partyName(budget, getEmployeeName)),
        gradient: passive
          ? 'tw-from-slate-400 tw-to-slate-600 tw-shadow-slate-500/30'
          : income ? 'tw-from-teal-400 tw-to-emerald-600 tw-shadow-emerald-500/30' : 'tw-from-rose-400 tw-to-orange-500 tw-shadow-rose-500/30',
        title: text(budget['BütçeAdı']) || partyName(budget, getEmployeeName) || '-',
        subtitle: [amountText === '-' ? '' : `${amountText}${currency ? ` ${currency}` : ''}`, formatDate(pick(budget, 'BelgeTarihi', 'BütçeTarihi'))].filter(Boolean).join(' · ') || undefined,
        badges: (
          <>
            <Badge tone={income ? 'info' : 'accent'}>{label(income ? 'types.income' : 'types.expense')}</Badge>
            <PaymentBadge value={budget['ÖdemeDurumu']} />
            <ActiveBadge passive={passive} />
          </>
        ),
      }}
      actions={actions}
      details={details}
      footer={{
        label: passive ? label('panel.activate') : label('panel.deactivate'),
        title: passive ? label('panel.activateHint') : label('panel.deactivateHint'),
        icon: passive ? LuBadgeCheck : LuBan,
        tone: passive ? 'success' : 'danger',
        onClick: onToggleActive,
      }}
      onPrev={onPrev}
      onNext={onNext}
      onClose={onClose}
    />
  );
};

BudgetDetailPane.propTypes = {
  docked: PropTypes.bool,
  height: PropTypes.number,
  budget: PropTypes.object,
  isAdmin: PropTypes.bool,
  canToggleStatus: PropTypes.bool,
  getEmployeeName: PropTypes.func.isRequired,
  position: PropTypes.shape({ index: PropTypes.number, total: PropTypes.number }),
  onPrev: PropTypes.func,
  onNext: PropTypes.func,
  onClose: PropTypes.func.isRequired,
  onToggleStatus: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onToggleActive: PropTypes.func.isRequired,
};

export default BudgetDetailPane;
